'use client';

import { RouteFailureScreen } from '@/components/system/RouteFailureScreen';
import { useRouteFailure } from '@/components/system/useRouteFailure';
import { getRunningVersion, reloadToVersion } from '@/lib/frontend-version';

type DashboardErrorProps = {
  error: Error & { digest?: string };
  reset: () => void;
};

function copyFor(kind: string): { title: string; message: string } {
  if (kind === 'update') {
    return {
      title: 'A new version was released',
      message: 'A new version was released. Reload to fetch the latest files. This page reloads once on its own, then waits for you to choose Reload or Back.',
    };
  }
  if (kind === 'stale-chunk') {
    return {
      title: 'This page could not load its scripts',
      message: 'The scripts for this page failed to load. This page reloads once on its own, then waits for you to choose Reload or Back.',
    };
  }
  if (kind === 'checking') {
    return {
      title: 'Opening this page',
      message: 'Checking whether a new version is available.',
    };
  }
  return {
    title: 'This page could not open',
    message: 'Something went wrong while opening this dashboard page. Reload to try again, or go back to your events.',
  };
}

export default function DashboardError({ error, reset }: DashboardErrorProps) {
  const failure = useRouteFailure(error, 'Dashboard route error');
  const copy = copyFor(failure.kind);
  const scriptFailure = failure.kind === 'update' || failure.kind === 'stale-chunk';

  return (
    <RouteFailureScreen
      chunkError={scriptFailure}
      title={copy.title}
      message={copy.message}
      onReload={() => {
        reloadToVersion(failure.deployed || getRunningVersion());
      }}
      onBack={() => {
        if (scriptFailure) {
          window.location.assign('/dashboard/events');
          return;
        }
        if (window.history.length > 1) {
          window.history.back();
          return;
        }
        reset();
        window.location.assign('/dashboard/events');
      }}
    />
  );
}
